import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserInfo, clearAuth, UserInfo } from '../../lib/auth';
import { PermissionCache } from '../../utils/permissionCache';
import { authService } from '../../services/api';

interface HeaderProps {
  onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    const user = getUserInfo();
    setCurrentUser(user);
  }, []);

  const handleLogout = () => {
    // 通知后端终止会话（清 httpOnly cookie）；失败不阻塞本地登出
    authService.logout().catch(() => undefined);
    clearAuth();
    PermissionCache.clear();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <header className="h-14 md:h-16 bg-white border-b border-slate-200 flex items-center justify-between px-3 md:px-6 flex-shrink-0">
        <div className="flex items-center gap-2 md:gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden w-9 h-9 md:w-10 md:h-10 flex items-center justify-center hover:bg-slate-100 rounded-lg transition-colors"
          >
            <i className="ri-menu-line text-lg md:text-xl text-slate-700"></i>
          </button>
          <h2 className="text-base md:text-lg font-semibold text-slate-800">工作台</h2>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {/* 用户信息 */}
          <div className="relative flex items-center gap-2 md:gap-3 pl-2 md:pl-4 border-l border-slate-200">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 md:gap-3 hover:bg-slate-100 rounded-lg px-2 py-1.5 transition-colors"
            >
              {currentUser?.avatar && !avatarError ? (
                <img
                  src={currentUser.avatar}
                  alt={currentUser.name || '用户'}
                  className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover ring-2 ring-cyan-400/20"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xs md:text-sm font-semibold">
                  {currentUser?.name?.charAt(0) || 'U'}
                </div>
              )}
              <div className="hidden md:block text-left">
                <p className="text-xs md:text-sm font-medium text-slate-800 truncate max-w-[120px]">{currentUser?.name || '用户'}</p>
                {currentUser?.phone && (
                  <p className="text-[10px] md:text-xs text-slate-500 truncate max-w-[120px]">{currentUser.phone}</p>
                )}
              </div>
              <i className="ri-arrow-down-s-line text-slate-400 hidden md:block"></i>
            </button>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                ></div>
                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden min-w-[200px]">
                  <div className="p-3 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      {currentUser?.avatar && !avatarError ? (
                        <img
                          src={currentUser.avatar}
                          alt={currentUser.name || '用户'}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                          onError={() => setAvatarError(true)}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                          {currentUser?.name?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{currentUser?.name || '用户'}</p>
                        <p className="text-xs text-slate-500 truncate">{currentUser?.email || currentUser?.phone || ''}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/profile');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer mb-1"
                    >
                      <i className="ri-user-line text-lg"></i>
                      <span className="text-sm font-medium">个人中心</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-line text-lg"></i>
                      <span className="text-sm font-medium">退出登录</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
